package ai.wagemix.logic

import co.touchlab.kermit.Severity
import com.fsryan.foundation.data.FSSyncEvent
import com.fsryan.foundation.models.value
import com.fsryan.foundation.util.log.FSLog
import com.fsryan.foundation.util.log.sendEventInclusive
import kotlin.time.Clock

/*
 * The analytics events more than one presenter emits (structured-logging playbook, troubleshooting
 * "two presenters emit the same event"), and the wire forms they share. Event names are "WM" plus
 * the PascalCase of the catalogue name in planning/analytics-plan.md §5; the site's analytics gate
 * forwards them under the snake_case name and adds the session and screen context.
 */

/**
 * The key under which a sync's start time (epoch milliseconds) rides in its presenter context, so
 * the sync's own success event says how long it took without the presenter remembering anything.
 */
internal const val WM_SYNC_KEY_STARTED_AT = "ai.wagemix.logic.STARTED_AT"

/** This presenter context plus the moment the sync starts, read back by [durationMsOf]. */
internal fun Map<String, Any>.withStartedAt(clock: Clock): Map<String, Any> {
    return this + (WM_SYNC_KEY_STARTED_AT to clock.now().toEpochMilliseconds())
}

/** The milliseconds since the sync of [event] started, or `null` when its context carries no start. */
internal fun FSSyncEvent<*>.durationMsOf(clock: Clock): Long? {
    val startedAt = value(Long::class, WM_SYNC_KEY_STARTED_AT) ?: return null
    return clock.now().toEpochMilliseconds() - startedAt
}

/** Where a stored mix was opened from (`mix_opened.from`). */
internal enum class WMMixOpenedFrom { Sidebar, SavedMixes }

private inline fun FSLog.sendWMAnalyticsEvent(
    eventName: String,
    throwable: Throwable? = null,
    crossinline fields: MutableMap<String, Any>.() -> Unit = {}
) {
    sendEventInclusive(throwable = throwable, severity = Severity.Info, eventName = eventName) {
        fields()
    }
}

internal fun FSLog.sendWMMixOpened(mixId: String, from: WMMixOpenedFrom) {
    sendWMAnalyticsEvent(eventName = "WMMixOpened") {
        put("mix_id", mixId)
        put(
            "from",
            when (from) {
                WMMixOpenedFrom.Sidebar -> "sidebar"
                WMMixOpenedFrom.SavedMixes -> "saved_mixes"
            }
        )
    }
}

internal fun FSLog.sendWMMixReevaluationRequested(mixId: String) {
    sendWMAnalyticsEvent(eventName = "WMMixReevaluationRequested") {
        put("mix_id", mixId)
    }
}

internal fun FSLog.sendWMMixReevaluationCompleted(mixId: String, durationMs: Long?) {
    sendWMAnalyticsEvent(eventName = "WMMixReevaluationCompleted") {
        put("mix_id", mixId)
        durationMs?.let { put("duration_ms", it) }
    }
}

internal fun FSLog.sendWMMixReevaluationFailed(mixId: String, error: Throwable) {
    sendWMAnalyticsEvent(eventName = "WMMixReevaluationFailed", throwable = error) {
        put("mix_id", mixId)
    }
}

/** Sent by the auth presenter (forgot password) and the account presenter (change password). */
internal fun FSLog.sendWMPasswordResetRequested() {
    sendWMAnalyticsEvent(eventName = "WMPasswordResetRequested")
}
